<template>
  <v-card
    class="mx-auto text-center rounded-lg"
    max-width="600"
    :disabled="isSaving"
  >
    <v-card-text>
      <div class="mb-4 text--secondary font-italic caption text-end">
        {{ currentData.isRequired ? '(obligatoire)' : '(optionnel)' }}
      </div>

      <v-img
        :src="require(`@/assets/${currentData.picture}.svg`)"
        height="100"
        contain
      ></v-img>

      <div class="title text--primary">{{ currentData.title }}</div>

      <div class="subtitle-1">{{ currentData.subtitle }}</div>
    </v-card-text>

    <v-window v-model="step" touchless>

      <!-- --------------------------------- MEMBER WINDOW ---------------------------------- -->
      <v-window-item :value="0">
        <v-card-text class="pt-0">
          <v-checkbox
            v-model="isMember"
            label="Oui, je suis déjà membre du personnel de Condorcet"
            :style="{ 'max-width' : '401px' }"
            class="mx-auto"
          ></v-checkbox>

          <v-radio-group
            v-model="candidType"
            mandatory
            :style="{ 'max-width' : '401px' }"
            class="mx-auto"
            v-if="isMember"
          >
            <template v-slot:label>
              <div>Cette candidature concerne :</div>
            </template>
            <v-radio
              label="Temporaire"
              value="Temporaire"
            ></v-radio>
            <v-radio
              label="Changement de fonction"
              value="Changement de fonction"
            ></v-radio>
            <v-radio
              label="Extension de charge"
              value="Extension de charge"
            ></v-radio>
            <v-radio
              label="Mutation"
              value="Mutation"
            ></v-radio>
            <v-radio
              label="Changement d'affectation"
              value="Changement d'affectation"
            ></v-radio>
            <v-radio
              label="Changement de cours à conférer"
              value="Changement de cours à conférer"
            ></v-radio>
          </v-radio-group>
        </v-card-text>
      </v-window-item>

      <!-- ------------------------------------ Charge ------------------------------------ -->

      <v-window-item :value="1">
        <v-select
          label="Charge"
          v-model="volume"
          :items="volumeArray"
          :suffix="jobTypeIsEns ? '/10' : '/38'"
          filled
          class="py-2 mx-auto"
          :style="{ 'max-width': '150px'}"
          rounded
        ></v-select>
      </v-window-item>

      <!-- ------------------------------------ Titres ------------------------------------ -->

      <v-window-item :value="2">
        <span v-if="errorMsg && degreeList.length === 0" class="caption error--text">
          Ajoutez au moins un diplôme pour continuer
        </span>

        <degree-list
          v-if="degreeList.length !== 0"
          :items="degreeList"
          :showActions="true"
          @remove-degree="removeDegree"
        ></degree-list>

        <v-card-text class="text-center">
          <degree-form
            :jobType="job.type_emploi"
            @adding-degree="addDegree"
          ></degree-form>
        </v-card-text>
      </v-window-item>

      <!-- ------------------------- Etablissements d'enseignement ------------------------ -->

      <v-window-item :value="3">
        <establishment-list
          v-if="establishmentList.length !== 0"
          :items="establishmentList"
          :showActions="true"
          :jobType="job.type_emploi"
          @remove-establishment="removeEstablishment"
        ></establishment-list>

        <v-card-text class="text-center">
          <establishment-form
            :jobType="job.type_emploi"
            @adding-establishment="addEstablishment"
          ></establishment-form>
        </v-card-text>
      </v-window-item>

      <!-- ------------------------------------ Annexes ----------------------------------- -->

      <v-window-item :value="4">
        <v-form ref="form" @submit.prevent>
          <v-file-input
            label="Annexes"
            v-model="files"
            :rules="rules.files"
            filled
            append-icon="mdi-paperclip"
            prepend-icon=""
            class="py-2 px-4"
            rounded
            clearable
            show-size
            multiple
            hint="Les annexes doivent être au format PDF"
            persistent-hint
          ></v-file-input>
        </v-form>
      </v-window-item>

      <!-- ----------------------------------- Confirmation --------------------------------- -->

      <v-window-item :value="5">
        <v-card-text class="text-center">
          <v-divider></v-divider>
          <div class="text-body-1 font-weight-bold">Informations</div>
          <v-divider></v-divider>
          <v-list class="text-left" two-line>
            <template v-if="jobTypeIsEns">
              <v-list-item>
                <v-list-item-icon>
                  <v-icon>mdi-account-group</v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>Membre de condorcet</v-list-item-title>

                  <v-list-item-subtitle class="text--primary">
                    {{ isMember ? 'Oui' : 'Non' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-list-item v-if="isMember">
                <v-list-item-icon>
                  <v-icon>mdi-file-question</v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>Type de candidature</v-list-item-title>

                  <v-list-item-subtitle class="text--primary">
                    {{ candidType }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>

            <v-list-item>
              <v-list-item-icon>
                <v-icon>mdi-abacus</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>Volume de la charge souhaitée</v-list-item-title>

                <v-list-item-subtitle class="text--primary">
                  {{ volume }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-divider></v-divider>
          <div class="text-body-1 font-weight-bold">Titres :</div>
          <v-divider></v-divider>
          <degree-list
            :items="degreeList"
            @remove-degree="removeDegree"
          ></degree-list>

          <template v-if="establishmentList.length !== 0">
            <v-divider></v-divider>
            <div class="text-body-1 font-weight-bold">Etablissements d'enseignement :</div>
            <v-divider></v-divider>
            <establishment-list
              :items="establishmentList"
              :jobType="job.type_emploi"
              @remove-establishment="removeEstablishment"
            ></establishment-list>
          </template>

          <template v-if="files.length !== 0">
            <v-divider></v-divider>
            <div class="text-body-1 font-weight-bold">Annexes :</div>
            <v-divider></v-divider>
            <v-list class="text-left" two-line>
              <v-list-item v-for="(file, index) in files" :key="index">
                <v-list-item-icon>
                  <v-icon>mdi-file</v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>{{ file.name }}</v-list-item-title>

                  <v-list-item-subtitle class="text--primary">
                    {{ formatBytes(file.size) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>

          <v-btn
            color="primary"
            block
            @click="send"
            :loading="isSaving"
          >
            Envoyer
          </v-btn>
        </v-card-text>
      </v-window-item>
    </v-window>

    <v-divider></v-divider>

    <!-- ----------------------------------- CARD ACTIONS ----------------------------------- -->
    <v-card-actions class="justify-space-between">
      <v-btn
        :disabled="step === (jobTypeIsEns ? 0 : 1)"
        @click="step--"
        text
      >
        <v-icon>
          mdi-chevron-left
        </v-icon>
      </v-btn>

      <v-item-group
        v-model="step"
        mandatory
      >
        <v-item
          v-for="n in (jobTypeIsEns ? 6 : 5)"
          :key="`btn-${n}`"
          v-slot="{ active }"
          disabled
          :value="jobTypeIsEns ? n - 1 : n"
        >
          <v-btn
            x-small
            :input-value="active"
            icon
            :color="jobTypeIsEns ? (n <= step + 1 ? 'primary' : '') : (n <= step ? 'primary' : '')"
          >
            <v-icon>mdi-record</v-icon>
          </v-btn>
        </v-item>
      </v-item-group>

      <v-btn
        :disabled="step === 5"
        @click="next"
        text
      >
        <v-icon>
          mdi-chevron-right
        </v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import DegreeForm from '@/components/apply/DegreeForm.vue';
import EstablishmentForm from '@/components/apply/EstablishmentForm.vue';
import DegreeList from '@/components/apply/DegreeList.vue';
import EstablishmentList from '@/components/apply/EstablishmentList.vue';

const axios = require('axios').default;

export default {
  name: 'ApplyCard',

  components: {
    'degree-form': DegreeForm,
    'establishment-form': EstablishmentForm,
    'degree-list': DegreeList,
    'establishment-list': EstablishmentList,
  },

  props: {
    job: Object,
  },

  created() {
    if (this.jobTypeIsEns) {
      this.volume = this.job.volume >= 10 ? 10 : this.job.volume;
      this.volumeArray = Array(this.volume).fill(1).map((e, i) => e + i);
    } else {
      if (this.job.volume === 19) {
        this.volume = 19;
        this.volumeArray.push(19);
      } else {
        this.volume = 38;
        this.volumeArray.push(19, 38);
      }
      this.step = 1;
    }
  },

  data() {
    return {
      volumeArray: [],
      candidType: 'Temporaire',
      isMember: false,
      isSaving: false,
      step: 0,
      volume: null,
      degreeList: [],
      establishmentList: [],
      files: [],
      errorMsg: false,
      rules: {
        files: [
          (v) => v.length < 11
          || 'Le maximum de 10 fichiers est dépassé',
          (v) => v.every((file) => file.type === 'application/pdf')
          || 'Tous les fichiers doivent être au format PDF',
          (v) => v.every((file) => file.size > 0)
          || 'Aucun fichier ne peut être vide',
          (v) => v.every((file) => file.size < 25000000)
          || 'Chaque fichier doit faire moins de 25 MB',
        ],
      },
    };
  },

  computed: {
    jobTypeIsEns() {
      return this.job.type_emploi === 'ENS';
    },
    currentData() {
      switch (this.step) {
        case 0: return {
          isRequired: false,
          picture: 'undraw_teaching_f1cm',
          title: 'Membre du personnel',
          subtitle: 'Êtes-vous déjà membre du personnel de Condorcet ?',
        };
        case 1: return {
          isRequired: true,
          picture: 'undraw_Work_time_re_hdyv',
          title: 'Charge',
          subtitle: 'Volume de la charge de travail souhaitée',
        };
        case 2: return {
          isRequired: true,
          picture: 'undraw_certification_aif8',
          title: 'Titres',
          subtitle: 'Vos diplômes tenant lieu de titre requis à fournir',
        };
        case 3: return {
          isRequired: false,
          picture: 'undraw_Graduation_ktn0',
          title: 'Etablissements d\'enseignement',
          subtitle: 'Etablissements d\'enseignement où vous êtes affecté',
        };
        case 4: return {
          isRequired: false,
          picture: 'undraw_add_document_0hek',
          title: 'Annexes',
          subtitle: 'CV, lettre de motivation, résumé de publications scientifiques, expériences professionnelles, ...',
        };
        default: return {
          isRequired: true,
          picture: 'undraw_Letter_re_8m03',
          title: 'Envoi',
          subtitle: 'Vérifiez vos informations et envoyez votre candidature',
        };
      }
    },
  },

  methods: {
    addDegree(degree) {
      this.degreeList.push({ ...degree });
    },

    addEstablishment(establishment) {
      this.establishmentList.push({ ...establishment });
    },

    removeDegree(degree) {
      const index = this.degreeList.indexOf(degree);
      this.degreeList.splice(index, 1);
    },

    removeEstablishment(establishment) {
      const index = this.establishmentList.indexOf(establishment);
      this.establishmentList.splice(index, 1);
    },

    async send() {
      this.isSaving = true;
      const data = this.createFormData();
      try {
        await axios.post('/postulations_emplois_vacants', data);
        this.$emit('confirm-sending');
      } catch (error) {
        this.$store.commit('SET_SNACKBAR', {
          isActive: true,
          text: 'Une erreur est survenue, votre candidature n\'a pas pu être envoyée',
        });
      } finally {
        this.isSaving = false;
      }
    },

    createFormData() {
      const data = new FormData();

      data.append('userId', this.$store.state.user.id);
      data.append('jobId', this.job.id);
      data.append('volume', this.volume);
      // INFOS
      if (this.jobTypeIsEns) {
        data.append('isMember', this.isMember ? 1 : 0);
        if (this.isMember) {
          data.append('candidType', this.candidType);
        }
      }
      // DEGREE LIST
      for (let i = 0; i < this.degreeList.length; i += 1) {
        const degree = this.degreeList[i];
        data.append('degreeList[][name]', degree.name);
        data.append('degreeList[][date]', degree.date);
        data.append('degreeList[][establishment]', degree.establishment);
        if (degree.file) {
          data.append('degreeList[][file]', degree.file);
        }
        data.append('degreeList[][experience_utile]', degree.mfp ? 1 : 0);
        data.append('degreeList[][isForeign]', degree.isForeign ? 1 : 0);
        if (degree.isForeign) {
          data.append('degreeList[][hasEquivalence]', degree.hasEquivalence ? 1 : 0);
          if (degree.hasEquivalence && degree.equivalence) {
            data.append('degreeList[][equivalence]', degree.equivalence);
          }
        }
      }
      // ESTABLISHMENT LIST
      for (let i = 0; i < this.establishmentList.length; i += 1) {
        const establishment = this.establishmentList[i];
        data.append('establishmentList[][name]', establishment.name);
        if (establishment.charge) {
          data.append('establishmentList[][charge]', establishment.charge);
        }
      }
      // FILES
      for (let i = 0; i < this.files.length; i += 1) {
        const file = this.files[i];
        data.append('files[]', file);
      }

      return data;
    },

    next() {
      if (this.step === 2 && this.degreeList.length === 0) {
        this.errorMsg = true;
      } else if (this.step === 4 && !this.$refs.form.validate()) {
        // pass
      } else {
        this.step += 1;
      }
    },

    formatBytes(bytes) {
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
    },
  },
};
</script>
